import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ListOptions from './ListOptions';
import { optimizeSearch, getOptions } from '../../Actions/FindMyPart.action';
import { translate } from '../../Services/translation';

const FindMyPartOptionsPsg = ({
  activeSegment,
  psgSearchItems,
  initBrandValue,
  initModelValue,
  ignoreSetCookie = false,
}) => {
  const dispatch = useDispatch();
  const brandsRef = useRef();
  const modelsRef = useRef();
  const unitRef = useRef();
  const sagRef = useRef();
  const standardUnitObject = {
    text: 'Si units',
    value: 'Si',
    selected: false,
  };
  const ridersWeightRef = useRef();
  const shockRef = useRef();
  const [selectedBrand, setSelectedBrand] = useState({});
  const [selectedModel, setSelectedModel] = useState({});
  const [selectedUnit, setSelectedUnit] = useState(standardUnitObject);
  const [selectedSag, setSelectedSag] = useState(-1);
  const [selectedRidersWeight, setSelectedRidersWeight] = useState(-1);
  const [selectedShock, setSelectedShock] = useState({});
  const [lastChangedOptionType, setLastChangedOptionType] = useState();
  const [searchActive, setSearchActive] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [lastSearchObj, setLastSearchObj] = useState({});
  const [isSettingModel, setIsSettingModel] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchValue, setSearchValue] = useState();
  const [currentWeightUnit, setCurrentWeightUnit] = useState('kg');
  const [currentLengthUnit, setCurrentLengthUnit] = useState('mm');
  const SiImperialCalcConstant = 2.20462;

  const getSelectedOptionsObject = () => {
    const options = {
      segment: activeSegment,
      lastChangedOptionType,
      optionsGeneral: null,
      ignoreSetCookie,
      optionsPsg: {
        brand: selectedBrand,
        model: selectedModel,
        unit: selectedUnit,
        sag: selectedSag,
        ridersWeight: selectedRidersWeight,
        shock: selectedShock,
      },
    };
    return options;
  };
  const doSearch = (searchQuery) => {
    const foundResult =
      !searchQuery || searchQuery.length < 1 || !psgSearchItems
        ? {}
        : psgSearchItems.search(searchQuery);
    setSearchResult(foundResult);
  };
  const setResultData = (obj) => {
    const brand = optionsPsg.brands.find((x) => x.text == obj.brand);
    if (brand) {
      setSelectedBrand(brand);
      setLastSearchObj(obj);
      setLastChangedOptionType('brand');
      setIsSettingModel(true);
    }
  };

  useEffect(() => {
    setSelectedBrand(initBrandValue);
  }, [initBrandValue]);
  useEffect(() => {
    setSelectedModel(initModelValue);
  }, [initModelValue]);

  useEffect(() => {
    if (selectedUnit?.value?.toLowerCase() != 'si') {
      setCurrentLengthUnit('mm');
      setCurrentWeightUnit('lb');
    } else {
      setCurrentLengthUnit('mm');
      setCurrentWeightUnit('kg');
    }
  }, [selectedUnit]);

  const optimize = () => {
    const options = getSelectedOptionsObject();
    dispatch(optimizeSearch(options));
  };
  const optionsPsg = useSelector(
    (state) => state.findMyPart.options?.optionsPsg
  );
  const setIntVal = (val, type) => {
    if (type === 'sag') {
      setSelectedSag(parseInt(val ?? -1));
    } else {
      setSelectedRidersWeight(parseInt(val ?? -1));
    }
  };

  // Convert kilograms to pounds and floor the result
  const getSelectedUnitWeight = (weight) => {
    if (selectedUnit?.value?.toLowerCase() != 'si') {
      return Math.floor(weight * SiImperialCalcConstant);
    }
    return weight;
  };

  // Convert pounds to kilograms and floor the result
  const getSiWeight = (weight) => {
    if (selectedUnit?.value?.toLowerCase() != 'si') {
      return Math.floor(weight / SiImperialCalcConstant);
    }
    return weight;
  };

  const getLengthUnit = () => {
    if (selectedUnit?.value?.toLowerCase() != 'si') {
      return 'in';
    }
    return 'mm';
  };

  const getMinSagValue = () => {
    return selectedShock?.text?.includes('mm') ? 1 : 15;
  };

  const getMaxSagValue = () => {
    return selectedShock?.text?.includes('mm') ? 100 : 50;
  };

  const getMinWeightValue = () => {
    return selectedUnit?.value?.toLowerCase() != 'si' ? 88 : 40;
  };
  const getPreferredSagUnit = () => {
    return selectedShock?.text?.includes('mm') ? currentLengthUnit : '%';
  };

  useEffect(() => {
    setSelectedBrand(initBrandValue || {});
    setSelectedModel(initModelValue || {});
    setLastChangedOptionType('');
  }, [activeSegment]);

  useEffect(() => {
    if (lastChangedOptionType) {
      const options = getSelectedOptionsObject();
      dispatch(getOptions(options));
    }
  }, [lastChangedOptionType, selectedBrand]);

  useEffect(() => {
    if (optionsPsg?.models.length > 0 && isSettingModel) {
      const model = optionsPsg.models.find(
        (x) => x.text == lastSearchObj.model
      );
      if (model) {
        setSelectedModel(model);
      }
      setIsSettingModel(false);
    }
  }, [optionsPsg?.models]);

  useEffect(() => {
    if (optionsPsg?.units.length > 0 && !selectedUnit?.value) {
      setSelectedUnit(optionsPsg.units[0]);
    }
  }, [optionsPsg?.units]);

  useEffect(() => {
    if (optionsPsg?.shock.length > 0 && !selectedShock?.value) {
      setSelectedShock(optionsPsg.shock[0]);
    }
  }, [optionsPsg?.shock]);

  const handleInput = (e) => {
    const val = e.target.value;
    doSearch(val);
    setSearchValue(val);
  };
  return (
    <Fragment>
      {optionsPsg && (
        <div className="row find-my-part__options-container">
          <div className=" large-11 columns">
            <div className="row">
              <div className="find-my-part__option small-12 large-4 columns">
                <label
                  htmlFor="fmpSearch"
                  className="product-info__variants-header"
                >
                  {translate('findmypart.search')}
                </label>
                <div
                  id="fmpSearch"
                  className="find-my-part__search-input-wrapper"
                >
                  <input
                    type="text"
                    className="searchInput"
                    value={searchValue || ''}
                    onChange={handleInput}
                    onFocus={() => setSearchActive(true)}
                    onBlur={() =>
                      setTimeout(() => {
                        setSearchActive(false);
                      }, 300)
                    }
                    placeholder={translate('findmypart.search')}
                  />
                  <button onClick={() => {}}></button>
                  {/* We can use the "status" to decide whether we should display the dropdown or not */}
                  {searchActive && searchResult?.length > 0 && (
                    <ul className="find-my-part__search-input-result">
                      {searchResult &&
                        searchResult.map((object, index) => (
                          <li
                            key={`result${index}`}
                            onClick={(e) => setResultData(object.item)}
                            className={`${
                              activeIndex === index ? 'active' : ''
                            }`}
                          >
                            {object.item.brand} {object.item.model_and_year}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="find-my-part__option small-12 large-4 columns">
                <ListOptions
                  id="fmpBrand"
                  title={translate('findmypart.brand')}
                  selectRef={brandsRef}
                  selectedOption={selectedBrand}
                  initVal={selectedBrand}
                  selectKey="psgBrands"
                  list={optionsPsg.brands}
                  setSelectedOption={(val) => {
                    setSelectedBrand(val);
                    setLastChangedOptionType('brand');
                  }}
                  defaultText={translate('findmypart.selectbrand')}
                />
              </div>
              <div className="find-my-part__option small-12 large-4 columns">
                <ListOptions
                  id="fmpModels"
                  title={translate('findmypart.model')}
                  selectRef={modelsRef}
                  selectedOption={selectedModel}
                  initVal={selectedModel}
                  list={optionsPsg.models}
                  setSelectedOption={(val) => setSelectedModel(val)}
                  defaultText={translate('findmypart.selectmodel')}
                />{' '}
              </div>
            </div>
            {selectedModel?.value?.length > 0 &&
              selectedUnit?.value?.length > 0 &&
              optionsPsg.models.find((x) => x.value == selectedModel.value) && (
                <div className="row">
                  <div className="find-my-part__option small-12 large-4 columns">
                    <div
                      className="find-my-part__description-text"
                      dangerouslySetInnerHTML={{
                        __html: optionsPsg.descriptionText,
                      }}
                    />
                  </div>

                  <div className="small-12 large-8 columns">
                    <div className="row">
                      <div className="find-my-part__option small-12 medium-6 columns">
                        <ListOptions
                          id="fmpUnit"
                          title={translate('findmypart.unit')}
                          cssClass="pad-top"
                          selectRef={unitRef}
                          selectedOption={selectedUnit}
                          list={optionsPsg.units}
                          setSelectedOption={(val) => {
                            setSelectedUnit(val);
                            setLastChangedOptionType('model');
                          }}
                        />
                      </div>
                      <div className="find-my-part__option small-12 medium-6 columns">
                        <label htmlFor="fmpRiderWeight" className="pad-top">
                          <h3 className="product-info__variants-header">
                            {translate('findmypart.ridersweight')}
                          </h3>
                        </label>
                        <input
                          id="fmpRiderWeight"
                          type="number"
                          className="find-my-part__select"
                          ref={ridersWeightRef}
                          placeholder={
                            translate('findmypart.minimum') +
                            ' ' +
                            getMinWeightValue()
                          }
                          min={getMinWeightValue()}
                          onChange={(e) => setIntVal(e.target.value, 'weight')}
                        />
                        <span className="find-my-part__input-unit">
                          {currentWeightUnit}
                        </span>
                      </div>
                    </div>
                    <div className="row">
                      <div className="find-my-part__option small-12 medium-6 columns">
                        <label htmlFor="fmpSag">
                          <h3 className="product-info__variants-header">
                            {translate('findmypart.prefferedsag') +
                              ' ' +
                              getPreferredSagUnit()}
                          </h3>
                        </label>
                        <input
                          id="fmpSag"
                          type="number"
                          ref={sagRef}
                          className="find-my-part__select"
                          placeholder={
                            translate('findmypart.minimum') +
                            ' ' +
                            getMinSagValue() +
                            ', ' +
                            translate('findmypart.max') +
                            ' ' +
                            getMaxSagValue()
                          }
                          min={getMinSagValue()}
                          max={getMaxSagValue()}
                          onChange={(e) => setIntVal(e.target.value, 'sag')}
                        />
                        <span className="find-my-part__input-unit">
                          {getPreferredSagUnit()}
                        </span>
                      </div>
                      <div className="find-my-part__option small-12 medium-6 columns">
                        <ListOptions
                          id="fmpShock"
                          //title={translate('findmypart.atshock')}
                          title={
                            selectedShock?.text?.replace(/[()]/g, '') ??
                            translate('findmypart.atshock')
                          }
                          selectRef={shockRef}
                          selectedOption={selectedShock}
                          list={optionsPsg?.shock}
                          setSelectedOption={(val) => setSelectedShock(val)}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="find-my-part__button-container columns small-12">
                        <button
                          className="wide"
                          id="fmpSubmit"
                          onClick={() => optimize()}
                          disabled={
                            !(
                              selectedModel?.value?.length > 0 &&
                              selectedUnit?.value?.length > 0 &&
                              selectedRidersWeight >= getMinWeightValue() &&
                              selectedSag >= getMinSagValue() &&
                              selectedSag <= getMaxSagValue() &&
                              selectedShock?.value?.length > 0
                            )
                          }
                        >
                          {translate('findmypart.optimize')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default FindMyPartOptionsPsg;
